<!-- eslint-disable perfectionist/sort-imports -->
<script setup lang="ts">
import { formatCpfMasked, normalizeCpfDigits } from '@solfacil/frontend-utils/cpf';
import { Download, Eye } from 'lucide-vue-next';
import { computed, ref } from 'vue';
import { toast } from 'vue3-toastify';

import DataTable from '~/components/shared/DataTable.vue';
import DeleteWithReasonModal from '~/components/shared/DeleteWithReasonModal.vue';
import PreviewDocumentModal from '~/components/shared/PreviewDocumentModal.vue';
import Tooltip from '~/components/shared/Tooltip.vue';
import type { RegisterAPI } from '~/services/register-api/register-api.types';
import { useDeleteAssociate } from '~/store/associates/useAssociateMutations';
import useRegisterPartnerDetails from '~/store/registerPartner/useRegisterPartnerDetails';

import AssociateModal from './AssociateModal.vue';

type Associate = RegisterAPI.Associate;
type PartnerDocument = RegisterAPI.PartnerDocument;

const props = defineProps<{
  companyId?: number;
  partnerId: number;
  usersPageSize?: number;
}>();

const TABLE_COLUMNS = [
  {
    key: 'name',
    label: 'Nome',
    sort: true,
    width: '35%',
  },
  {
    key: 'email',
    label: 'E-mail',
    sort: true,
    width: '30%',
  },
  {
    key: 'cpf',
    label: 'CPF',
    sort: false,
    width: '22%',
  },
  {
    key: 'identification',
    label: 'Documento',
    sort: false,
    width: '13%',
  },
];

const partnerIdRef = computed(() => props.partnerId);

const {
  data: partnerDetails,
  isLoading: isLoadingAssociates,
  refetch: refetchAssociates,
} = useRegisterPartnerDetails(partnerIdRef);

const deleteMutation = useDeleteAssociate();

const isAssociateModalVisible = ref(false);
const modalMode = ref<'edit' | 'view'>('view');
const selectedAssociate = ref<Associate | null>(null);

const isDeleteModalVisible = ref(false);
const associateForDelete = ref<Associate | null>(null);

const isPreviewVisible = ref(false);
const previewUrl = ref<null | string>(null);
const previewTitle = ref('Visualizar documento');

const tableData = computed(() => partnerDetails.value?.associates ?? []);

function downloadDocument(document: PartnerDocument) {
  window.open(document.url, '_blank');
}

async function handleConfirmDelete(reason: string) {
  if (!associateForDelete.value) return;

  try {
    if (!props.companyId) {
      toast.error('Empresa não encontrada para inativar o sócio.');
      return;
    }

    await deleteMutation.mutateAsync({
      companyId: props.companyId,
      data: {
        document: normalizeCpfDigits(associateForDelete.value.cpf),
        reason,
      },
    });
    isDeleteModalVisible.value = false;
    associateForDelete.value = null;
  } catch {
    // handled by mutation
  }
}

function handleDeleteCancel() {
  associateForDelete.value = null;
}

function openAssociateDelete(associate: Associate) {
  associateForDelete.value = associate;
  isDeleteModalVisible.value = true;
}

function openAssociateDetails(associate: Associate) {
  modalMode.value = 'view';
  selectedAssociate.value = associate;
  isAssociateModalVisible.value = true;
}

function openAssociateEdit(associate: Associate) {
  modalMode.value = 'edit';
  selectedAssociate.value = associate;
  isAssociateModalVisible.value = true;
}

function openDocumentPreview(document: PartnerDocument, ownerName: string) {
  previewUrl.value = document.url_inline;
  previewTitle.value = document.filename || `Documento de ${ownerName}`;
  isPreviewVisible.value = true;
}
</script>

<template>
  <div class="col-12 mb-3">
    <div class="card">
      <div class="card-header">
        <h3 class="card-title">
          Sócios - <small class="text-muted fw-normal">(os sócios são sempre vinculados a um usuário)</small>
        </h3>
      </div>
      <div class="card-body py-3">
        <DataTable
          :columns="TABLE_COLUMNS"
          :data="tableData"
          :loading="isLoadingAssociates"
          :actions="['details', 'edit', 'delete']"
          row-key="cpf"
          empty-message="Nenhum sócio encontrado"
          @delete="openAssociateDelete"
          @details="openAssociateDetails"
          @edit="openAssociateEdit"
          @row-dblclick="openAssociateDetails"
        >
          <template #cell-cpf="{ item }">
            <span class="text-secondary">
              {{ (item as Associate).cpf ? formatCpfMasked(normalizeCpfDigits((item as Associate).cpf)) : '—' }}
            </span>
          </template>

          <template #cell-identification="{ item }">
            <div v-if="(item as Associate).identification" class="d-flex gap-1">
              <Tooltip content="Visualizar documento">
                <button
                  type="button"
                  class="btn btn-sm btn-outline-primary"
                  aria-label="Visualizar documento"
                  @click="openDocumentPreview(
                    (item as Associate).identification!,
                    (item as Associate).name,
                  )"
                >
                  <Eye class="icon icon-sm" />
                </button>
              </Tooltip>
              <Tooltip content="Baixar documento">
                <button
                  type="button"
                  class="btn btn-sm btn-outline-secondary"
                  aria-label="Baixar documento"
                  @click="downloadDocument((item as Associate).identification!)"
                >
                  <Download class="icon icon-sm" />
                </button>
              </Tooltip>
            </div>
            <span v-else class="text-secondary">—</span>
          </template>
        </DataTable>
      </div>
    </div>

    <AssociateModal
      v-model="isAssociateModalVisible"
      :mode="modalMode"
      :partner-id="partnerId"
      :company-id="companyId"
      :users-page-size="usersPageSize ?? 10"
      :selected-associate="selectedAssociate"
      :associates="tableData"
      @success="refetchAssociates"
    />

    <DeleteWithReasonModal
      v-model="isDeleteModalVisible"
      title="Inativar sócio"
      confirm-text="Inativar"
      description="Você está prestes a inativar o sócio abaixo. Esta ação requer um motivo."
      :name="associateForDelete?.name || ''"
      :loading="deleteMutation.isPending.value"
      @confirm="handleConfirmDelete"
      @cancel="handleDeleteCancel"
    />

    <PreviewDocumentModal
      v-model="isPreviewVisible"
      :title="previewTitle"
      :url="previewUrl"
    />
  </div>
</template>
